import React, { useEffect, useState } from 'react';
import {
  View,
  TouchableOpacity,
  StyleSheet,
  useWindowDimensions,
  ScrollView,
  Platform,
} from 'react-native';
import Text from '../../components/text';
import Feather from '@expo/vector-icons/Feather';
import drawerHoc from '../../components/drawerHOC/DrawerHOC';
import { COLORS, BORDER_RADIUS, SHADOWS } from '../../constants/theme';
import Terms from './terms';
import Privacy from './privacy';
import Cookies from './cookies';

const TABS = [
  { key: 'terms', label: 'Terms & Conditions', shortLabel: 'Terms', icon: 'file-text' },
  { key: 'privacy', label: 'Privacy Policy', shortLabel: 'Privacy', icon: 'shield' },
  { key: 'cookies', label: 'Cookies', shortLabel: 'Cookies', icon: 'cpu' },
];

const LegalScreen = (props) => {
  const { width } = useWindowDimensions();
  const isMobile = width <= 767;

  const getInitialTab = () => {
    const tabParam =
      props?.route?.params?.tab ||
      (Platform.OS === 'web' && new URLSearchParams(window.location.search).get('tab'));
    if (tabParam === 'privacy') return 'privacy';
    if (tabParam === 'cookies') return 'cookies';
    return 'terms';
  };

  const [activeTab, setActiveTab] = useState(getInitialTab);

  useEffect(() => {
    const tabParam = props?.route?.params?.tab;
    if (tabParam === 'privacy') setActiveTab('privacy');
    else if (tabParam === 'cookies') setActiveTab('cookies');
    else if (tabParam === 'terms') setActiveTab('terms');
  }, [props?.route?.params?.tab]);

  const handleTabPress = (tabKey) => {
    setActiveTab(tabKey);
    if (Platform.OS === 'web') {
      window.history.replaceState({}, '', `/legal-bits?tab=${tabKey}`);
    }
  };

  const renderContent = () => {
    if (activeTab === 'privacy') return <Privacy />;
    if (activeTab === 'cookies') return <Cookies />;
    return <Terms />;
  };

  return (
    <ScrollView
      style={[styles.container, isMobile && styles.containerMobile]}
      showsVerticalScrollIndicator={false}
    >
      {/* Breadcrumb Header */}
      <Text style={styles.breadcrumbText}>Settings › Legal Bits</Text>

      <View style={styles.headerRow}>
        <Text style={styles.pageTitle}>Legal Bits</Text>
        <Text style={styles.pageSubtitle}>
          Review our platform policies including Terms, Privacy, and Cookies.
        </Text>
      </View>

      {/* Info Banner Card */}
      <View style={styles.infoBanner}>
        <View style={styles.infoBannerIcon}>
          <Feather name="book-open" size={18} color="#6C2BD9" />
        </View>
        <View style={styles.infoBannerText}>
          <Text style={styles.infoBannerTitle}>Platform Legal Documents</Text>
          <Text style={styles.infoBannerDesc}>
            These documents govern your use of the Hubita platform. Please read them carefully to understand your rights and obligations.
          </Text>
        </View>
      </View>

      {/* Tab Navigation — equal-width tabs, purple bottom border on active */}
      <View style={styles.tabsBar}>
        {TABS.map((tab) => {
          const isActive = activeTab === tab.key;
          return (
            <TouchableOpacity
              key={tab.key}
              style={[styles.tabBtn, isActive && styles.tabBtnActive]}
              onPress={() => handleTabPress(tab.key)}
              activeOpacity={0.8}
            >
              <Feather
                name={tab.icon}
                size={14}
                color={isActive ? '#6C2BD9' : '#9CA3AF'}
                style={{ marginBottom: 2 }}
              />
              <Text style={[styles.tabLabel, isActive && styles.tabLabelActive]}>
                {isMobile ? tab.shortLabel : tab.label}
              </Text>
            </TouchableOpacity>
          );
        })}
      </View>

      {/* Content Card */}
      <View style={styles.contentCard}>
        {renderContent()}
      </View>
    </ScrollView>
  );
};

export default drawerHoc({
  Component: LegalScreen,
  showBackButton: true,
  backNavigation: 'Setting',
});

/* ================= STYLES ================= */

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
    backgroundColor: COLORS.appBg,
  },
  containerMobile: {
    padding: 14,
  },

  breadcrumbText: {
    fontSize: 12,
    color: '#9CA3AF',
    marginBottom: 6,
  },

  headerRow: {
    marginBottom: 18,
  },

  pageTitle: {
    fontSize: 24,
    fontWeight: '700',
    color: COLORS.textPrimary,
    marginBottom: 4,
  },

  pageSubtitle: {
    fontSize: 13,
    color: '#6B7280',
  },

  /* INFO BANNER */
  infoBanner: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    backgroundColor: '#F5F3FF',
    borderWidth: 1,
    borderColor: '#E0D9FA',
    borderRadius: BORDER_RADIUS.lg,
    padding: 16,
    marginBottom: 20,
  },
  infoBannerIcon: {
    width: 36,
    height: 36,
    borderRadius: 8,
    backgroundColor: '#FFFFFF',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 14,
    flexShrink: 0,
  },
  infoBannerText: {
    flex: 1,
  },
  infoBannerTitle: {
    fontSize: 13,
    fontWeight: '700',
    color: '#6C2BD9',
    marginBottom: 4,
  },
  infoBannerDesc: {
    fontSize: 12,
    color: '#4B5563',
    lineHeight: 18,
  },

  /* TABS */
  tabsBar: {
    flexDirection: 'row',
    backgroundColor: '#FFFFFF',
    borderRadius: BORDER_RADIUS.lg,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    marginBottom: 16,
    overflow: 'hidden',
    ...SHADOWS.sm,
  },

  tabBtn: {
    flex: 1,
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 14,
    paddingHorizontal: 8,
    borderBottomWidth: 3,
    borderBottomColor: 'transparent',
    minHeight: 56,
  },
  tabBtnActive: {
    borderBottomColor: '#6C2BD9',
    backgroundColor: '#FAFAFE',
  },

  tabLabel: {
    fontSize: 12,
    fontWeight: '600',
    color: '#9CA3AF',
    textAlign: 'center',
    marginTop: 2,
  },
  tabLabelActive: {
    color: '#6C2BD9',
  },

  /* CONTENT CARD */
  contentCard: {
    backgroundColor: COLORS.surface,
    borderRadius: BORDER_RADIUS.lg,
    borderWidth: 1,
    borderColor: COLORS.border,
    padding: 24,
    marginBottom: 40,
    ...SHADOWS.sm,
  },
});
